import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconAkte, IconEntfernen, IconPlus } from '../components/icons';
import { DEMO, api, ApiFehler, getToken } from '../lib/api';
import { DEMO_OBJEKTE } from '../lib/demodaten';
import { formatDatum } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

const ARTEN = [
  { wert: 'vertrag', label: 'Mietvertrag / Vertrag' },
  { wert: 'beleg', label: 'Beleg / Rechnung' },
  { wert: 'protokoll', label: 'Protokoll' },
  { wert: 'abrechnung', label: 'Abrechnung' },
  { wert: 'zaehlerfoto', label: 'Zählerfoto' },
  { wert: 'sonstiges', label: 'Sonstiges' },
] as const;
const ART_LABEL: Record<string, string> = Object.fromEntries(ARTEN.map((a) => [a.wert, a.label]));

interface ObjektRef {
  id: string;
  bezeichnung: string;
}
interface Dokument {
  id: string;
  objektId: string | null;
  bezugTyp: string;
  titel: string | null;
  dateiName: string | null;
  dateiMime: string | null;
  bytes: number | null;
  hatDatei: boolean;
  createdAt: string | null;
}

const DEMO_DOKUMENTE: Dokument[] = [
  {
    id: 'dok-1',
    objektId: 'obj-1',
    bezugTyp: 'vertrag',
    titel: 'Mietvertrag Berger, Wohnung 1',
    dateiName: 'mietvertrag-berger.pdf',
    dateiMime: 'application/pdf',
    bytes: 184000,
    hatDatei: true,
    createdAt: '2025-01-12',
  },
  {
    id: 'dok-2',
    objektId: 'obj-1',
    bezugTyp: 'sonstiges',
    titel: 'Grundriss Gebäude',
    dateiName: 'grundriss.pdf',
    dateiMime: 'application/pdf',
    bytes: 512000,
    hatDatei: true,
    createdAt: '2024-11-03',
  },
];

function groesse(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function Dokumente() {
  const [objekte, setObjekte] = useState<ObjektRef[]>(
    DEMO ? DEMO_OBJEKTE.map((o) => ({ id: o.id, bezeichnung: o.bezeichnung })) : [],
  );
  const [dokumente, setDokumente] = useState<Dokument[] | null>(DEMO ? DEMO_DOKUMENTE : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    let aktiv = true;
    setLaden(true);
    Promise.all([
      api.get<{ objekte: ObjektRef[] }>('/objekte'),
      api.get<{ dokumente: Dokument[] }>('/dokumente'),
    ])
      .then(([o, d]) => {
        if (!aktiv) return;
        setObjekte(o.objekte);
        setDokumente(d.dokumente);
        setFehler(null);
      })
      .catch((e) => aktiv && setFehler(e instanceof ApiFehler ? e.message : 'Dokumente konnten nicht geladen werden.'))
      .finally(() => aktiv && setLaden(false));
    return () => {
      aktiv = false;
    };
  }, []);

  function objektName(id: string | null): string | null {
    if (!id) return null;
    return objekte.find((o) => o.id === id)?.bezeichnung ?? null;
  }

  async function oeffnen(dok: Dokument) {
    if (DEMO) return;
    try {
      const res = await fetch(`/api/dokumente/${dok.id}/datei`, {
        headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      /* ignorieren */
    }
  }

  async function loeschen(id: string) {
    if (!DEMO) {
      try {
        await api.del(`/dokumente/${id}`);
      } catch {
        return;
      }
    }
    setDokumente((ds) => (ds ?? []).filter((d) => d.id !== id));
  }

  return (
    <div>
      <Seitenkopf
        titel="Dokumente"
        beschreibung="Der digitale Aktenordner: Mietverträge, Bescheide, Pläne und Schriftverkehr — abfotografiert oder gescannt, revisionssicher an einem Ort abgelegt."
        aktion={
          <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)}>
            Dokument hochladen
          </Button>
        }
      />

      {formOffen && (
        <DokumentFormular
          objekte={objekte}
          onHochgeladen={(d) => {
            setDokumente((ds) => [d, ...(ds ?? [])]);
            setFormOffen(false);
          }}
          onAbbruch={() => setFormOffen(false)}
        />
      )}

      {laden && !dokumente ? (
        <p className="text-sm text-fg-muted">Dokumente werden geladen …</p>
      ) : fehler && !dokumente ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : dokumente && dokumente.length === 0 ? (
        <EmptyState
          icon={<IconAkte groesse={22} />}
          titel="Noch keine Dokumente"
          beschreibung="Laden Sie Mietverträge, Bescheide oder Pläne hoch — als PDF oder Foto vom Handy."
          aktion={<Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen(true)}>Dokument hochladen</Button>}
        />
      ) : (
        <div className="space-y-2.5">
          {(dokumente ?? []).map((d) => (
            <Card key={d.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => oeffnen(d)}
                  className="flex min-w-0 items-center gap-3 text-left"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent-soft-fg">
                    <IconAkte groesse={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-fg hover:underline">
                      {d.titel ?? d.dateiName ?? 'Dokument'}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-fg-muted">
                      {objektName(d.objektId) ? `${objektName(d.objektId)} · ` : ''}
                      {d.dateiName ?? ''} {groesse(d.bytes) ? `· ${groesse(d.bytes)}` : ''}
                      {d.createdAt ? ` · ${formatDatum(d.createdAt)}` : ''}
                    </span>
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge ton="neutral">{ART_LABEL[d.bezugTyp] ?? d.bezugTyp}</Badge>
                  <Button
                    variante="dezent"
                    groesse="sm"
                    aria-label="Löschen"
                    icon={<IconEntfernen groesse={16} />}
                    onClick={() => loeschen(d.id)}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DokumentFormular({
  objekte,
  onHochgeladen,
  onAbbruch,
}: {
  objekte: ObjektRef[];
  onHochgeladen: (d: Dokument) => void;
  onAbbruch: () => void;
}) {
  const [objektId, setObjektId] = useState('');
  const [bezugTyp, setBezugTyp] = useState('vertrag');
  const [titel, setTitel] = useState('');
  const [datei, setDatei] = useState<{ name: string; mime: string; base64: string } | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const dateiRef = useRef<HTMLInputElement>(null);

  function dateiGewaehlt(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const leser = new FileReader();
    leser.onload = () => {
      const s = String(leser.result);
      const base64 = s.includes(',') ? s.slice(s.indexOf(',') + 1) : s;
      setDatei({ name: f.name, mime: f.type || 'application/octet-stream', base64 });
      if (!titel) setTitel(f.name.replace(/\.[^.]+$/, ''));
    };
    leser.readAsDataURL(f);
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!datei) {
      setFehler('Bitte eine Datei auswählen (PDF oder Foto).');
      return;
    }
    setSendet(true);
    try {
      if (DEMO) {
        onHochgeladen({
          id: `neu-${Date.now()}`,
          objektId: objektId || null,
          bezugTyp,
          titel: titel.trim() || datei.name,
          dateiName: datei.name,
          dateiMime: datei.mime,
          bytes: Math.floor((datei.base64.length * 3) / 4),
          hatDatei: true,
          createdAt: new Date().toISOString().slice(0, 10),
        });
        return;
      }
      const antwort = await api.post<{ dokument: Dokument }>('/dokumente', {
        objektId: objektId || undefined,
        bezugTyp,
        titel: titel.trim() || undefined,
        dateiBase64: datei.base64,
        dateiName: datei.name,
        dateiMime: datei.mime,
      });
      onHochgeladen(antwort.dokument);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Dokument konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Dokument hochladen" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt (optional)</span>
            <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              <option value="">— ohne Objekt —</option>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Art</span>
            <select className={feldKlasse} value={bezugTyp} onChange={(e) => setBezugTyp(e.target.value)}>
              {ARTEN.map((a) => (
                <option key={a.wert} value={a.wert}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <Input label="Titel (optional)" value={titel} onChange={(e) => setTitel(e.target.value)} />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-fg">Datei</span>
          <input
            ref={dateiRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={dateiGewaehlt}
            className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-accent-soft file:px-3 file:py-2 file:text-sm file:font-medium file:text-accent-soft-fg"
          />
          {datei && <p className="mt-1 text-xs text-fg-subtle">Gewählt: {datei.name}</p>}
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Dokument speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Dokumente;
